import { queryOptions, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query';
import { servicesApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { assetKeys, type AssetListParams } from './entity';
import { parseAssetsListResponse, populateAssetsList, type ParsedAssetsListResponse } from './populate';

/**
 * Asset read queries.
 *
 * Assets only have a list endpoint (no by-id detail endpoint). The list
 * endpoint is folder-scoped — pass `folder_id` to browse into a folder.
 * Each row is populated into the detail cache so downstream consumers
 * can read individual assets by id without re-fetching.
 */

const STALE_TIME = 60_000;

/* ------------------------------------------------------------------------- */
/* listAssets — paginated, folder-scoped                                      */
/* ------------------------------------------------------------------------- */

/** @public */
export async function listAssets(
  workspaceId: number,
  params: AssetListParams,
  qc: QueryClient,
): Promise<ParsedAssetsListResponse> {
  const raw = await servicesApi.listAssets({
    params: { environment_id: workspaceId },
    query: {
      folder_id: params.folder_id,
      page: params.page,
      search: params.search,
      sort: params.sort,
      order: params.order,
      limit: params.limit,
    },
  });
  const response = parseAssetsListResponse(raw);
  populateAssetsList(qc, workspaceId, response);
  return response;
}

export const listAssetsQueryOptions = (workspaceId: number, params: AssetListParams, qc: QueryClient) =>
  queryOptions({
    queryKey: assetKeys.list(workspaceId, params),
    queryFn: () => listAssets(workspaceId, params, qc),
    staleTime: STALE_TIME,
  });

interface UseAssetsListOptions {
  params: AssetListParams;
  queryConfig?: QueryConfig<typeof listAssetsQueryOptions>;
}

/** @public */
export const useAssetsList = ({ params, queryConfig }: UseAssetsListOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...listAssetsQueryOptions(workspaceId, params, qc),
    ...queryConfig,
  });
};
